import { isValidElement, ReactElement, ReactNode, useEffect, useState } from "react";
import { isTMessage, TMessage, TMessageParam } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { when } from "@/utils/functions";
import { AutoSizer } from "./AutoSizer";
import { Box, Card, VBox } from "./Box";
import { InfoButton } from "./InfoButton";
import { DataTable, DataTableProps } from "./DataTable";
import { LinkButton } from "./Button";
import { IconName } from "./iconMapping";
import { DCEvent } from "@/events/eventsDomain";
import { IconColor } from "./Icon";
import { Markdown } from "@/text/Markdown";
import { PieChart, PieChartProps, Sparkline, SparklineProps } from "./Sparkline";
import { Popover } from "./Popover";
import { observer } from "mobx-react";

export type DataCardInfoPopoverContentTypes<T> = ["msg", TMessage] | ["table", DataTableProps<T>];

export type DataCardInfoPopoverProps<T> = {
  content: TMessage | DataCardInfoPopoverContentTypes<T>;
  iconColor?: IconColor;
  icon?: IconName;
};

export const DataCardInfoPopover = <T,>(props: DataCardInfoPopoverProps<T>) => {
  const color = props.iconColor ?? "subtle";
  const [type, content]: DataCardInfoPopoverContentTypes<T> = isTMessage(props.content)
    ? ["msg", props.content]
    : props.content;
  switch (type) {
    case "msg":
      return <InfoButton infoText={content} color={color} />;
    case "table":
      return (
        <InfoButton
          infoText={<DataTable {...content} />}
          disableMaxWidth
          color={color}
          icon={props.icon}
        />
      );
  }
};

export type DataCardProps<T = unknown> = {
  title: TMessage;
  subtitle?: TMessage | TMessageParam;
  infoPopover?: DataCardInfoPopoverProps<T>;
  extraInfo?: TMessage | ReactElement;
  value?: TMessage | TMessageParam | string;
  sparkline?: SparklineProps<T>;
  chart?: { type: "sparkline"; sparkline: SparklineProps<T> } | { type: "pie"; pie: PieChartProps };
  table?: DataTableProps<T>;
  text?: string;
  action?: { label: TMessage; icon?: IconName; onClick: DCEvent[] | (() => void) };
  className?: string;
  children?: ReactNode;
  short?: boolean;
};

export const DataCard = observer(<T,>(props: DataCardProps<T>) => {
  const { t, tf } = useTranslation();
  const [extraInfoEl, setExtraInfoEl] = useState<HTMLDivElement | null>(null);
  const [showReadMoreButton, setShowReadMoreButton] = useState(false);
  const extraInfo = isTMessage(props.extraInfo) ? tf(props.extraInfo) : null;

  useEffect(() => {
    if (extraInfoEl) {
      setShowReadMoreButton(extraInfoEl.scrollHeight > extraInfoEl.clientHeight);
    }
  }, [extraInfoEl, extraInfo]);

  return (
    <Card gap={"md"} className={props.className} grow overflow={"hidden"}>
      <Box justifyContent={"space-between"} alignItems={"start"}>
        <div>
          <div>
            <strong>{t(props.title)}</strong>
          </div>
          {props.subtitle && <small className={"subtle"}>{t(props.subtitle)}</small>}
        </div>
        <Box alignItems={"center"} gap={"lg"}>
          {props.action && <LinkButton {...props.action} />}
          {props.infoPopover && <DataCardInfoPopover {...props.infoPopover} />}
        </Box>
      </Box>
      {props.text && <Markdown value={props.text} />}
      {(props.value || props.chart) && (
        <VBox grow gap={"md"}>
          {props.value && (
            <Box alignItems={"center"}>
              <div className={"text-h3"}>{tf(props.value)}</div>
              {props.sparkline && (
                <div className={"color-primary grow h-10"}>
                  <AutoSizer>{(size) => <Sparkline {...size} {...props.sparkline!} />}</AutoSizer>
                </div>
              )}
            </Box>
          )}
          {!props.short && (props.value || props.chart) && (
            <div className={classNames(["color-primary", "grow", props.value ? "h-10" : "h-38"])}>
              {when(props.chart, (chart) => (
                <AutoSizer>
                  {(size) => {
                    switch (chart.type) {
                      case "pie":
                        return <PieChart {...size} {...chart.pie} />;
                      case "sparkline":
                        return <Sparkline {...size} {...chart.sparkline} />;
                    }
                  }}
                </AutoSizer>
              ))}
            </div>
          )}
        </VBox>
      )}
      {props.table && <DataTable {...props.table} variant={"IN_CARD"} />}
      {props.children}
      {isValidElement(props.extraInfo) && <Box>{props.extraInfo}</Box>}
      {extraInfo && (
        <div>
          <div ref={setExtraInfoEl} className={"line-clamp-3"}>
            {extraInfo}
          </div>
          {showReadMoreButton && (
            <>
              <Popover
                button={<LinkButton label={t("show-more")} />}
                content={
                  <VBox maxWidth={"prose"}>
                    <Markdown value={extraInfo} />
                  </VBox>
                }
              />
            </>
          )}
        </div>
      )}
    </Card>
  );
});
